import { useEffect, useMemo } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { prefetchAvatars } from './components/PersonAvatar.js';
import { SettingsButton } from './components/SettingsButton.js';
import { UndoSnackbar } from './components/UndoSnackbar.js';
import { useDocuments } from './lib/documents.js';
import { offlineEnabled, useOfflineVault } from './lib/offlineFiles.js';
import { usePeople } from './lib/people.js';
import { tripPhase, useTrips } from './lib/travel.js';
import { UndoProvider } from './lib/undoToast.js';

/**
 * Silent app-level offline-vault syncer. Rendered only when the
 * per-device switch is on, so devices that never enabled it don't
 * subscribe to the documents query from the layout. Keeps the pinned
 * copies fresh no matter which page the session starts on — the Vault
 * card is the switch, this is the upkeep. Documents linked to a trip
 * that hasn't ended join the set, so tickets work in a dead spot.
 */
function OfflineVaultSync() {
  const documents = useDocuments();
  const trips = useTrips();
  const activeTripIds = useMemo(
    () =>
      new Set(
        (trips.data ?? [])
          .filter((t) => tripPhase(t, new Date()) !== 'past')
          .map((t) => t.id),
      ),
    [trips.data],
  );
  useOfflineVault(documents.data, activeTripIds);
  return null;
}

interface LayoutProps {
  appName: string;
  tagline: string;
}

export function Layout({ appName, tagline }: LayoutProps) {
  // Warm the avatar caches once per session, app-wide — every page that
  // shows people (People, Documents folders, pickers) then paints
  // photos instantly instead of fetching per page.
  const people = usePeople();
  useEffect(() => {
    prefetchAvatars(people.data);
  }, [people.data]);

  return (
    <UndoProvider>
      {offlineEnabled() && <OfflineVaultSync />}
      <div className="app-shell">
        <div className="app-container">
          <header className="site-header">
            {/* The wordmark doubles as the home button. With the tab
                row removed, tapping the title is how you return to the
                Today home screen from any sub-page (Obligations also
                carries its own back chevron). */}
            <Link to="/" className="site-header__home" aria-label="Almanac — home">
              <h1 className="site-header__title">{appName}</h1>
              <p className="site-header__subtitle">{tagline}</p>
            </Link>
            <SettingsButton />
          </header>
          <main>
            <Outlet />
          </main>
        </div>
        <UndoSnackbar />
      </div>
    </UndoProvider>
  );
}
